import { MenuContextProvider } from "@ubnt/store.core/lib/contexts/MenuContext";
import { MicrositeMenuContextProvider } from "@ubnt/store.core/lib/contexts/MicrositeMenuContext";
import { SiteId } from "@ubnt/store.core/lib/contexts/MenuContext/mapping";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { Fragment, useEffect, useMemo, Suspense, lazy } from "react";
import { MicrositeProductPageContextProvider } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";
import { GlobalHeader } from "../Header";
import { useSiteSettings } from "../../context/SiteSettingsContext/context";
import { Footer } from "../Footer";
import {
  MainContainer,
  SectionContainer,
  MainContainerContent,
} from "./layout";
import "../../styles/fonts.scss";
import {
  DEFAULT_LANGUAGE,
  GLOBAL_SITE_CONFIG,
} from "context/SiteSettingsContext/constants";
import { NOT_FOUND_PATH } from "context/SiteSettingsContext/utils";
import { getLanguageDirection } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import { VwoDebug } from "components/VwoDebug";
import { useBuyButton } from "hooks/useBuyButton";
import { useRemoveTrailingSlash } from "util/RemoveTrailingSlash";
import withTracker from "util/withTracker";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { StoreId } from "types";
import SubRouteLayout from "pages/LayoutComponents/SubRouteLayout";
import { ROUTE_MAP, REDIRECT_MAP } from "./mapping";
import { LocalizedRouteGroup } from "./LocalizedRouteGroup";

const LandingLazy = lazy(() =>
  import("pages/Sections/Landing").then((p) => ({
    default: p.Landing,
  })),
);

const Landing = withTracker(LandingLazy);
const NotFound = lazy(() => import("pages/NotFound/index"));

const DynamicRedirect = ({ to }: { to: string }) => {
  const { storeId = StoreId.Us, language = DEFAULT_LANGUAGE } = useParams();
  const basePath = `/${storeId}/${language}`;
  return <Navigate to={`${basePath}${to}`} replace />;
};

const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
};

const ROUTES_FROM_MAPPING = Object.values(ROUTE_MAP).map((mapping) => (
  <Fragment key={mapping.id}>
    {LocalizedRouteGroup({
      path: mapping.path,
      element: <SubRouteLayout mapping={mapping} />,
      splat: true,
    })}
  </Fragment>
));

const LANDING_ROUTE = (
  <Fragment>
    {LocalizedRouteGroup({ path: "/", element: <Landing /> })}
    <Route key={NOT_FOUND_PATH} path={NOT_FOUND_PATH} element={<NotFound />} />
  </Fragment>
);

const REDIRECT_ROUTES = REDIRECT_MAP.map(({ from, to }) => {
  const isExternal = to.startsWith("http");
  const RedirectComponent = isExternal ? ExternalRedirect : DynamicRedirect;
  const hasSplat = from.endsWith("/*");
  const path = hasSplat ? from.slice(0, -2) : from;

  return (
    <Fragment key={`${from}-${to}-redirect`}>
      {LocalizedRouteGroup({
        path,
        element: <RedirectComponent to={to} />,
        splat: hasSplat,
      })}
    </Fragment>
  );
});

gsap.registerPlugin(useGSAP);

const App = () => {
  useRemoveTrailingSlash();

  const location = useLocation();
  const { store, language, isAppView } = useSiteSettings();
  const { showBuyButton } = useBuyButton();

  const menuContextProps = useMemo(
    () => ({
      pathname: location.pathname,
      hash: location.hash,
      storeId: store.id,
      context: SiteId.Ui,
      globalSiteConfig: GLOBAL_SITE_CONFIG,
      language,
    }),
    [location.pathname, location.hash, store.id, language],
  );

  // #############################################################################
  // Update `<html lang="...">`  on language change, right now it always EN on first render
  // #############################################################################
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  return (
    <MenuContextProvider {...menuContextProps}>
      <MicrositeMenuContextProvider {...menuContextProps}>
        <MicrositeProductPageContextProvider showBuyButton={showBuyButton}>
          <VwoDebug />
          <MainContainer>
            <MainContainerContent>
              {/* Hide the global footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
              {!isAppView && <GlobalHeader />}
              <SectionContainer dir={getLanguageDirection(language)}>
                <Suspense fallback={null}>
                  <Routes>
                    {/* Section routes must come before root routes so /cloud-gateways/resource-calculator
                        matches /cloud-gateways/* instead of /:storeId (which would capture "cloud-gateways") */}
                    {ROUTES_FROM_MAPPING}
                    {REDIRECT_ROUTES}
                    {LANDING_ROUTE}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </SectionContainer>
            </MainContainerContent>

            {/* Hide the footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
            {!isAppView && <Footer />}
          </MainContainer>
        </MicrositeProductPageContextProvider>
      </MicrositeMenuContextProvider>
    </MenuContextProvider>
  );
};

export default App;
